// Dollars ⇄ integer cents at the UI boundary (Sep 2026).
//
// Storage, the API, the audit history and every loyalty calculation stay
// in integer cents — that is the authoritative unit and this module does
// not change it. What it changes is what a tenant types: a restaurant
// owner configuring "spend $100 to earn a point" should type `100.00`,
// not `10000`.
//
// The conversion is done on the STRING the tenant typed, never through
// binary floating point. `parseFloat("1.05") * 100` is 104.99999999999999
// in IEEE-754, and rounding hides that only until a value where it does
// not. Splitting the decimal string and assembling the integer directly
// has no such failure mode, so a load/save round-trip is exactly stable.

/** `10000` → `"100.00"`. Empty string when the value is unreadable. */
export function centsToDollarInput(cents) {
  const n = Number(cents);
  if (cents == null || cents === "" || !Number.isFinite(n)) return "";
  const whole = Math.trunc(Math.abs(Math.round(n)) / 100);
  const frac = Math.abs(Math.round(n)) % 100;
  const sign = Math.round(n) < 0 ? "-" : "";
  return `${sign}${whole}.${String(frac).padStart(2, "0")}`;
}

/** `$100.00` for display in explanatory text. */
export function formatDollarsFromCents(cents) {
  const text = centsToDollarInput(cents);
  return text === "" ? "" : `$${text}`;
}

// A non-negative decimal amount with at most two decimal places. No
// grouping separators, no currency symbol, no exponent: a tenant types a
// price, and anything else is rejected rather than guessed at.
const MONEY_RE = /^\d+(?:\.\d{1,2})?$/;

/** True when `text` is an amount this boundary will accept. */
export function isValidDollarInput(text) {
  return MONEY_RE.test(String(text ?? "").trim());
}

/**
 * `"1.05"` → `105`. Returns `null` for anything invalid — empty, blank,
 * non-numeric, negative, or carrying more than two decimal places.
 *
 * A third decimal place is a REJECTION, never a silent round: the tenant
 * typed a figure the system cannot store, and quietly storing a different
 * one is how a pricing rule ends up not being the rule they set.
 */
export function parseDollarsToCents(text) {
  const raw = String(text ?? "").trim();
  if (!MONEY_RE.test(raw)) return null;
  const [whole, frac = ""] = raw.split(".");
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, "0"));
  return Number.isFinite(cents) ? cents : null;
}
